import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FloppyDisk, Plus, Trash, Star, MapPin, User, CreditCard, ClockCounterClockwise, NotePencil } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../lib/auth';
import { getCustomer, createCustomer, updateCustomer, deleteCustomer, getCustomerHistory, getConfigOptions, getCountries, addConfigValue } from '../lib/api';
import { toast } from 'sonner';

function MultiSelect({ options, value = [], onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const toggle = (item) => {
    onChange(value.includes(item) ? value.filter(v => v !== item) : [...value, item]);
  };
  return (
    <div className="relative">
      <div className="min-h-[38px] border rounded-md px-2 py-1.5 flex flex-wrap gap-1 cursor-pointer bg-white" onClick={() => setOpen(!open)}>
        {value.length === 0 && <span className="text-gray-400 text-sm py-0.5">{placeholder}</span>}
        {value.map(v => (
          <Badge key={v} variant="secondary" className="text-xs gap-1">
            {v}
            <button onClick={e => { e.stopPropagation(); toggle(v); }} className="hover:text-red-500">&times;</button>
          </Badge>
        ))}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
          {options.filter(o => o.is_active !== false).map(o => (
            <label key={o.label || o} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
              <input type="checkbox" checked={value.includes(o.label || o)} onChange={() => toggle(o.label || o)} className="rounded" />
              {o.label || o}
            </label>
          ))}
          {options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No options</div>}
        </div>
      )}
    </div>
  );
}

function TagInput({ value = [], onChange, configType }) {
  const [input, setInput] = useState('');
  const addTag = async () => {
    const tag = input.trim();
    if (!tag || value.includes(tag)) { setInput(''); return; }
    onChange([...value, tag]);
    try { await addConfigValue(configType, tag); } catch {}
    setInput('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1">
        {value.map(t => (
          <Badge key={t} variant="secondary" className="text-xs gap-1">
            {t}
            <button onClick={() => onChange(value.filter(v => v !== t))} className="hover:text-red-500">&times;</button>
          </Badge>
        ))}
      </div>
      <Input placeholder="Add tag and press Enter" value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); }}}
        className="text-sm" />
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const isNew = id === 'new';
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');
  const [history, setHistory] = useState(null);
  const [countries, setCountries] = useState([]);
  const [configs, setConfigs] = useState({});

  const emptyCustomer = {
    company_name: '', display_name: '', customer_types: [], channels: [],
    country: '', default_currency: 'EUR', default_billing_entity: '', source: '',
    lifecycle_stage: 'Lead', tags: [],
    credit: { payment_terms: '', credit_limit_amount: 0, credit_limit_currency: 'EUR', credit_status: 'Not Evaluated', credit_notes: '' },
    billing_address: { line1: '', line2: '', city: '', state: '', postal_code: '', country: '', tax_id: '', tax_id_type: '' },
    shipping_addresses: [],
    contacts: [],
    internal_notes: '',
  };
  const [form, setForm] = useState(emptyCustomer);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const [types, channels, sources, stages, terms, statuses, currencies, roles, entities, taxTypes, tags, ctry] = await Promise.all([
          getConfigOptions('customer_types'), getConfigOptions('channels'),
          getConfigOptions('customer_sources'), getConfigOptions('lifecycle_stages'),
          getConfigOptions('payment_terms'), getConfigOptions('credit_statuses'),
          getConfigOptions('currencies'), getConfigOptions('contact_roles'),
          getConfigOptions('billing_entities'), getConfigOptions('tax_id_types'),
          getConfigOptions('customer_tags'), getCountries(),
        ]);
        setConfigs({
          customer_types: types.data.values, channels: channels.data.values,
          customer_sources: sources.data.values, lifecycle_stages: stages.data.values,
          payment_terms: terms.data.values, credit_statuses: statuses.data.values,
          currencies: currencies.data.values, contact_roles: roles.data.values,
          billing_entities: entities.data.values, tax_id_types: taxTypes.data.values,
          customer_tags: tags.data.values,
        });
        setCountries(ctry.data.countries);
      } catch {}
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (!isNew) {
      getCustomer(id).then(res => setForm(res.data)).catch(() => toast.error('Customer not found'));
      getCustomerHistory(id).then(res => setHistory(res.data)).catch(() => {});
    }
  }, [id, isNew]);

  const setField = (path, value) => {
    setForm(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = copy;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await createCustomer(form);
        toast.success('Customer created');
        navigate(`/customers/${res.data.id}`);
      } else {
        await updateCustomer(id, form);
        toast.success('Customer updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving customer');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Deactivate this customer? This will not delete linked quotes or challans.')) return;
    try {
      await deleteCustomer(id);
      toast.success('Customer deactivated');
      navigate('/customers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const addContact = () => setField('contacts', [...form.contacts, { id: crypto.randomUUID(), name: '', role: '', email: '', phone: '', whatsapp: '', is_primary: form.contacts.length === 0 }]);
  const removeContact = (idx) => setField('contacts', form.contacts.filter((_, i) => i !== idx));
  const setContact = (idx, key, val) => {
    const c = [...form.contacts];
    c[idx] = { ...c[idx], [key]: val };
    if (key === 'is_primary' && val) c.forEach((ct, i) => { if (i !== idx) ct.is_primary = false; });
    setField('contacts', c);
  };

  const addShipping = () => setField('shipping_addresses', [...form.shipping_addresses, { id: crypto.randomUUID(), label: '', line1: '', line2: '', city: '', state: '', postal_code: '', country: '', contact_person: '', contact_phone: '', is_default: form.shipping_addresses.length === 0 }]);
  const removeShipping = (idx) => setField('shipping_addresses', form.shipping_addresses.filter((_, i) => i !== idx));
  const setShipping = (idx, key, val) => {
    const a = [...form.shipping_addresses];
    a[idx] = { ...a[idx], [key]: val };
    if (key === 'is_default' && val) a.forEach((s, i) => { if (i !== idx) s.is_default = false; });
    setField('shipping_addresses', a);
  };

  const canEdit = isNew ? can('create_customers') : can('edit_customers');
  const activeOptions = (list) => (list || []).filter(v => v.is_active !== false);

  return (
    <div className="space-y-6" data-testid="customer-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild><Link to="/customers"><ArrowLeft size={18} /></Link></Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{isNew ? 'New Customer' : form.company_name || 'Customer'}</h1>
            {!isNew && form.lifecycle_stage && <Badge className="text-xs mt-1">{form.lifecycle_stage}</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && can('delete_customers') && (
            <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-customer-btn">
              <Trash size={16} className="mr-1" />Deactivate
            </Button>
          )}
          {canEdit && (
            <Button onClick={handleSave} disabled={saving} data-testid="save-customer-btn">
              <FloppyDisk size={18} className="mr-2" />{saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview"><User size={14} className="mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="contacts"><User size={14} className="mr-1" />Contacts ({form.contacts?.length || 0})</TabsTrigger>
          <TabsTrigger value="addresses"><MapPin size={14} className="mr-1" />Addresses</TabsTrigger>
          <TabsTrigger value="credit"><CreditCard size={14} className="mr-1" />Credit</TabsTrigger>
          {!isNew && <TabsTrigger value="history"><ClockCounterClockwise size={14} className="mr-1" />History</TabsTrigger>}
          <TabsTrigger value="notes"><NotePencil size={14} className="mr-1" />Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Company Name *</Label><Input value={form.company_name} onChange={e => setField('company_name', e.target.value)} disabled={!canEdit} data-testid="company-name" /></div>
            <div><Label>Display Name</Label><Input value={form.display_name} onChange={e => setField('display_name', e.target.value)} disabled={!canEdit} placeholder="Short name for lists" /></div>
            <div>
              <Label>Country</Label>
              <Select value={form.country} onValueChange={v => setField('country', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default Currency</Label>
              <Select value={form.default_currency} onValueChange={v => setField('default_currency', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activeOptions(configs.currencies).map(c => <SelectItem key={c.key} value={c.label}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Customer Types</Label><MultiSelect options={activeOptions(configs.customer_types)} value={form.customer_types} onChange={v => setField('customer_types', v)} placeholder="Select types" /></div>
            <div><Label>Channels</Label><MultiSelect options={activeOptions(configs.channels)} value={form.channels} onChange={v => setField('channels', v)} placeholder="Select channels" /></div>
            <div>
              <Label>Default Billing Entity</Label>
              <Select value={form.default_billing_entity} onValueChange={v => setField('default_billing_entity', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>
                  {activeOptions(configs.billing_entities).map(e => <SelectItem key={e.key} value={e.label}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={v => setField('source', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  {activeOptions(configs.customer_sources).map(s => <SelectItem key={s.key} value={s.label}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lifecycle Stage</Label>
              <Select value={form.lifecycle_stage} onValueChange={v => setField('lifecycle_stage', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activeOptions(configs.lifecycle_stages).map(s => <SelectItem key={s.key} value={s.label}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Tags</Label><TagInput value={form.tags || []} onChange={v => setField('tags', v)} configType="customer_tags" /></div>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4 mt-4">
          {(form.contacts || []).map((c, idx) => (
            <div key={c.id || idx} className="border rounded-lg p-4 space-y-3 relative" data-testid={`contact-${idx}`}>
              {c.is_primary && <Badge className="absolute top-2 right-12 text-xs bg-amber-100 text-amber-800"><Star size={12} className="mr-1" />Primary</Badge>}
              {canEdit && <Button variant="ghost" size="sm" className="absolute top-2 right-2 text-red-400 hover:text-red-600" onClick={() => removeContact(idx)}><Trash size={14} /></Button>}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Name</Label><Input value={c.name} onChange={e => setContact(idx, 'name', e.target.value)} disabled={!canEdit} /></div>
                <div>
                  <Label className="text-xs">Role</Label>
                  <Select value={c.role} onValueChange={v => setContact(idx, 'role', v)} disabled={!canEdit}>
                    <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
                    <SelectContent>
                      {activeOptions(configs.contact_roles).map(r => <SelectItem key={r.key} value={r.label}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Email</Label><Input type="email" value={c.email} onChange={e => setContact(idx, 'email', e.target.value)} disabled={!canEdit} /></div>
                <div><Label className="text-xs">Phone</Label><Input value={c.phone} onChange={e => setContact(idx, 'phone', e.target.value)} disabled={!canEdit} /></div>
                <div><Label className="text-xs">WhatsApp</Label><Input value={c.whatsapp} onChange={e => setContact(idx, 'whatsapp', e.target.value)} disabled={!canEdit} /></div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!c.is_primary} onChange={e => setContact(idx, 'is_primary', e.target.checked)} disabled={!canEdit} className="rounded" />
                    Primary contact
                  </label>
                </div>
              </div>
            </div>
          ))}
          {canEdit && <Button variant="outline" onClick={addContact} data-testid="add-contact"><Plus size={16} className="mr-1" />Add Contact</Button>}
        </TabsContent>

        <TabsContent value="addresses" className="space-y-6 mt-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Billing Address</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border rounded-lg p-4">
              <div className="col-span-2"><Label className="text-xs">Address Line 1</Label><Input value={form.billing_address?.line1 || ''} onChange={e => setField('billing_address.line1', e.target.value)} disabled={!canEdit} /></div>
              <div><Label className="text-xs">Line 2</Label><Input value={form.billing_address?.line2 || ''} onChange={e => setField('billing_address.line2', e.target.value)} disabled={!canEdit} /></div>
              <div><Label className="text-xs">City</Label><Input value={form.billing_address?.city || ''} onChange={e => setField('billing_address.city', e.target.value)} disabled={!canEdit} /></div>
              <div><Label className="text-xs">State</Label><Input value={form.billing_address?.state || ''} onChange={e => setField('billing_address.state', e.target.value)} disabled={!canEdit} /></div>
              <div><Label className="text-xs">Postal Code</Label><Input value={form.billing_address?.postal_code || ''} onChange={e => setField('billing_address.postal_code', e.target.value)} disabled={!canEdit} /></div>
              <div>
                <Label className="text-xs">Country</Label>
                <Select value={form.billing_address?.country || ''} onValueChange={v => setField('billing_address.country', v)} disabled={!canEdit}>
                  <SelectTrigger><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent className="max-h-60">{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Tax ID</Label><Input value={form.billing_address?.tax_id || ''} onChange={e => setField('billing_address.tax_id', e.target.value)} disabled={!canEdit} placeholder="e.g., NL123456789B01" /></div>
              <div>
                <Label className="text-xs">Tax ID Type</Label>
                <Select value={form.billing_address?.tax_id_type || ''} onValueChange={v => setField('billing_address.tax_id_type', v)} disabled={!canEdit}>
                  <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>{activeOptions(configs.tax_id_types).map(t => <SelectItem key={t.key} value={t.label}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Shipping Addresses</h3>
            {(form.shipping_addresses || []).map((a, idx) => (
              <div key={a.id || idx} className="border rounded-lg p-4 space-y-3 mb-3 relative" data-testid={`shipping-${idx}`}>
                {a.is_default && <Badge className="absolute top-2 right-12 text-xs bg-blue-100 text-blue-800">Default</Badge>}
                {canEdit && <Button variant="ghost" size="sm" className="absolute top-2 right-2 text-red-400" onClick={() => removeShipping(idx)}><Trash size={14} /></Button>}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="col-span-2"><Label className="text-xs">Label</Label><Input value={a.label} onChange={e => setShipping(idx, 'label', e.target.value)} disabled={!canEdit} placeholder='e.g., "Main Warehouse - Rotterdam"' /></div>
                  <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!a.is_default} onChange={e => setShipping(idx, 'is_default', e.target.checked)} disabled={!canEdit} className="rounded" />Default</label></div>
                  <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={a.line1} onChange={e => setShipping(idx, 'line1', e.target.value)} disabled={!canEdit} /></div>
                  <div><Label className="text-xs">City</Label><Input value={a.city} onChange={e => setShipping(idx, 'city', e.target.value)} disabled={!canEdit} /></div>
                  <div>
                    <Label className="text-xs">Country</Label>
                    <Select value={a.country || ''} onValueChange={v => setShipping(idx, 'country', v)} disabled={!canEdit}>
                      <SelectTrigger><SelectValue placeholder="Country" /></SelectTrigger>
                      <SelectContent className="max-h-60">{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Contact Person</Label><Input value={a.contact_person} onChange={e => setShipping(idx, 'contact_person', e.target.value)} disabled={!canEdit} /></div>
                  <div><Label className="text-xs">Contact Phone</Label><Input value={a.contact_phone} onChange={e => setShipping(idx, 'contact_phone', e.target.value)} disabled={!canEdit} /></div>
                </div>
              </div>
            ))}
            {canEdit && <Button variant="outline" onClick={addShipping} data-testid="add-shipping"><Plus size={16} className="mr-1" />Add Shipping Address</Button>}
          </div>
        </TabsContent>

        <TabsContent value="credit" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4">
            <div>
              <Label>Payment Terms</Label>
              <Select value={form.credit?.payment_terms || ''} onValueChange={v => setField('credit.payment_terms', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue placeholder="Select terms" /></SelectTrigger>
                <SelectContent>{activeOptions(configs.payment_terms).map(t => <SelectItem key={t.key} value={t.label}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Credit Status</Label>
              <Select value={form.credit?.credit_status || ''} onValueChange={v => setField('credit.credit_status', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{activeOptions(configs.credit_statuses).map(s => <SelectItem key={s.key} value={s.label}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Credit Limit</Label><Input type="number" value={form.credit?.credit_limit_amount || ''} onChange={e => setField('credit.credit_limit_amount', parseFloat(e.target.value) || 0)} disabled={!canEdit} /></div>
            <div>
              <Label>Limit Currency</Label>
              <Select value={form.credit?.credit_limit_currency || 'EUR'} onValueChange={v => setField('credit.credit_limit_currency', v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{activeOptions(configs.currencies).map(c => <SelectItem key={c.key} value={c.label}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Credit Notes (internal)</Label><Textarea value={form.credit?.credit_notes || ''} onChange={e => setField('credit.credit_notes', e.target.value)} disabled={!canEdit} rows={3} /></div>
          </div>
        </TabsContent>

        {!isNew && (
          <TabsContent value="history" className="mt-4 space-y-6">
            {history ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="border rounded-lg p-4 text-center">
                    <div className="text-xs text-gray-500 mb-1">Total Quoted</div>
                    {Object.entries(history.summary.total_quoted || {}).map(([cur, amt]) => (
                      <div key={cur} className="font-semibold text-lg">{cur} {amt.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                    ))}
                    {Object.keys(history.summary.total_quoted || {}).length === 0 && <div className="text-gray-400">-</div>}
                  </div>
                  <div className="border rounded-lg p-4 text-center">
                    <div className="text-xs text-gray-500 mb-1">Total Won</div>
                    {Object.entries(history.summary.total_won || {}).map(([cur, amt]) => (
                      <div key={cur} className="font-semibold text-lg text-green-700">{cur} {amt.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                    ))}
                    {Object.keys(history.summary.total_won || {}).length === 0 && <div className="text-gray-400">-</div>}
                  </div>
                  <div className="border rounded-lg p-4 text-center">
                    <div className="text-xs text-gray-500 mb-1">Quotes</div>
                    <div className="font-semibold text-lg">{history.summary.quote_count}</div>
                  </div>
                  <div className="border rounded-lg p-4 text-center">
                    <div className="text-xs text-gray-500 mb-1">Challans Dispatched</div>
                    <div className="font-semibold text-lg">{history.summary.total_dispatched_challans}</div>
                  </div>
                </div>

                {history.quotes.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Linked Quotes ({history.quotes.length})</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr>
                          <th className="text-left px-3 py-2">Quote</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-right px-3 py-2">Amount</th>
                          <th className="text-left px-3 py-2">Date</th>
                        </tr></thead>
                        <tbody>
                          {history.quotes.map(q => (
                            <tr key={q.id} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href = `/quotes/${q.id}`}>
                              <td className="px-3 py-2 font-medium">{q.quote_number}</td>
                              <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{q.status}</Badge></td>
                              <td className="px-3 py-2 text-right">{q.currency} {(q.total_amount || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-gray-500">{q.created_at?.slice(0, 10)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {history.challans.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Linked Challans ({history.challans.length})</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr>
                          <th className="text-left px-3 py-2">Challan</th>
                          <th className="text-left px-3 py-2">Type</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">Date</th>
                        </tr></thead>
                        <tbody>
                          {history.challans.map(c => (
                            <tr key={c.id} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href = `/challans/${c.id}`}>
                              <td className="px-3 py-2 font-medium">{c.challan_number}</td>
                              <td className="px-3 py-2">{c.challan_type}</td>
                              <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{c.status}</Badge></td>
                              <td className="px-3 py-2 text-gray-500">{c.challan_date?.slice(0, 10)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-400">Loading history...</div>
            )}
          </TabsContent>
        )}

        <TabsContent value="notes" className="mt-4">
          <Textarea placeholder="Internal notes about this customer..." value={form.internal_notes || ''} onChange={e => setField('internal_notes', e.target.value)} disabled={!canEdit} rows={8} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
